import AdLocationRegistry from './AdLocationRegistry';

const CORDLESS_SCRIPT_URL = (publisherId) => `https://ads.cordlessmedia.com/ad-wrapper/${publisherId}/cm.min.js`;

let scriptLoaded = false;
let scriptLoadingPromise = null;

function loadCordlessScript(publisherId) {
    // Check if ads should be blocked before loading script
    if (AdLocationRegistry.shouldBlockAds()) {
        console.log('[AdServiceCordless] Cordless Media script blocked due to ad blocking policy');
        return Promise.resolve(); // Return resolved promise but don't load script
    }
    // Check if script is already loaded by checking for existing script tag or cmWrapper
    if (scriptLoaded || window.cmWrapper || document.querySelector(`script[src*="cm.min.js"]`)) {
        scriptLoaded = true;
        return Promise.resolve();
    }

    if (scriptLoadingPromise) return scriptLoadingPromise;

    scriptLoadingPromise = new Promise((resolve, reject) => {
        // Double-check for existing script before creating new one
        const existingScript = document.querySelector(`script[src*="cm.min.js"]`);
        if (existingScript) {
            scriptLoaded = true;
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = CORDLESS_SCRIPT_URL(publisherId);
        script.async = true;
        script.setAttribute('data-cordless-main', 'true'); // Mark as main cordless script

        script.onload = () => {
            // Wait a bit for cmWrapper to be fully initialized
            const checkCmWrapper = (attempts = 0) => {
                if (window.cmWrapper && window.cmWrapper.ads) {
                    scriptLoaded = true;
                    resolve();
                } else if (attempts < 20) {
                    setTimeout(() => checkCmWrapper(attempts + 1), 50);
                } else {
                    console.warn('[AdServiceCordless] cmWrapper not available after script load');
                    scriptLoaded = true; // Mark as loaded anyway to prevent infinite retries
                    resolve();
                }
            };

            checkCmWrapper();
        };

        script.onerror = (error) => {
            console.error('[AdServiceCordless] Failed to load Cordless script:', error);
            scriptLoaded = false;
            scriptLoadingPromise = null; // Reset to allow retry
            reject(error);
        };

        document.head.appendChild(script);
    });

    return scriptLoadingPromise;
}

export default class AdServiceCordless {
    constructor({ publisherId, entityType, entityId, cache = {} }) {
        this.publisherId = publisherId;
        this.entityType = entityType;
        this.entityId = entityId;
        this.cache = cache;
        this.promiseCache = {};
        this.ads = {}; // Add direct ads object for easier access

        // Proactively load the Cordless script to ensure it's ready when needed
        this.loadScript();
    }

    // Public method to load the script
    async loadScript() {
        if (!this.publisherId) return;

        // Check if ads should be blocked before attempting to load
        if (AdLocationRegistry.shouldBlockAds()) {
            console.log('[AdServiceCordless] Skipping Cordless Media script load due to ad blocking policy');
            return;
        }

        try {
            await loadCordlessScript(this.publisherId);
        } catch (error) {
            console.error('[AdServiceCordless] Failed to load script:', error);
        }
    }

    processAdTemplates(ads, entityData) {
        // Process ad templates with actual data
        return ads.map((ad) => {
            if (ad.callback) {
                // Replace placeholder values in callback
                let callback = ad.callback;

                // Only replace values that exist
                if (entityData.state) {
                    callback = callback.replace(/#state#/g, entityData.state);
                } else {
                    callback = callback.replace(/#state#/g, '');
                }

                if (entityData.city) {
                    callback = callback.replace(/#city#/g, entityData.city);
                } else {
                    callback = callback.replace(/#city#/g, '');
                }

                if (entityData.zip) {
                    callback = callback.replace(/#zip#/g, entityData.zip);
                } else {
                    callback = callback.replace(/#zip#/g, '');
                }

                if (entityData.address) {
                    callback = callback.replace(/#address#/g, entityData.address);
                } else {
                    callback = callback.replace(/#address#/g, '');
                }

                if (entityData.price) {
                    callback = callback.replace(/#price#/g, entityData.price);
                } else {
                    callback = callback.replace(/#price#/g, '');
                }

                // Return processed ad
                return {
                    ...ad,
                    callback
                };
            }
            return ad;
        });
    }

    async fetchAds(locations) {
        if (!Array.isArray(locations)) locations = [];

        // Check if ads should be blocked before proceeding
        if (AdLocationRegistry.shouldBlockAds()) {
            console.log('[AdServiceCordless] Ads fetch blocked due to ad blocking policy');
            return []; // Return empty array to prevent any ad loading
        }

        // Ensure script is loaded (this will be a no-op if already loaded)
        await this.loadScript();

        const cacheKey = `${this.entityType}_${this.entityId}_${locations.sort().join('_')}`;

        if (this.promiseCache[cacheKey]) {
            await this.promiseCache[cacheKey];
        }

        if (!locations.every((loc) => Array.isArray(this.cache[`${this.entityType}_${this.entityId}_${loc}`]))) {
            this.promiseCache[cacheKey] = (async () => {
                const url = `/api/cta-setta-3000?entity_type=${this.entityType}&entity_id=${this.entityId}` + locations.map((l) => `&locations[]=${l}`).join('');
                try {
                    const res = await fetch(url);
                    const response = await res.json();
                    const adsArray = response.data || [];

                    // Get entity data for targeting
                    const entityData = {
                        state: window.searchLocation?.state || '',
                        zip: window.searchLocation?.zip || '',
                        address: window.searchLocation?.query || '',
                        price: window.searchLocation?.price || ''
                    };

                    // Process ad templates with actual data
                    const processedAds = this.processAdTemplates(adsArray, entityData);

                    // Store processed ads in direct ads object for easier access
                    processedAds.forEach((ad) => {
                        if (ad.html) {
                            const match = ad.html.match(/id=['"]([^'"]+)['"]/);
                            if (match) {
                                const adId = match[1];
                                this.ads[adId] = ad;
                            }
                        }
                    });

                    // Store in cache by location
                    processedAds.forEach((adObj) => {
                        const locKey = `${this.entityType}_${this.entityId}_${adObj.location}`;
                        if (!Array.isArray(this.cache[locKey])) this.cache[locKey] = [];
                        this.cache[locKey].push(adObj);
                    });

                } catch (error) {
                    console.error('[AdServiceCordless] Error fetching ads:', error);
                }
            })();
            await this.promiseCache[cacheKey];
        }

        // Return all ads for all requested locations as a flat array
        let allAds = [];
        locations.forEach((loc) => {
            const locKey = `${this.entityType}_${this.entityId}_${loc}`;
            if (Array.isArray(this.cache[locKey])) {
                allAds = allAds.concat(this.cache[locKey]);
            }
        });

        return allAds;
    }
}
